import React from 'react';
import Immutable from 'immutable';

import config from 'config/global';
import LHX from 'utils/LHX';
import { range, serialize } from 'utils/utils';

import * as popoverActions from './popoverActions';
import * as baseStateActions from './baseStateActions';
import * as dimensionsAvailabilityActions from './dimensionsAvailabilityActions';
import * as dimensionsActions from './dimensionsActions';
import * as appStateActions from './appStateActions';
import * as searchActions from './searchActions';
import { CALL_API, Schemas } from '../middleware/api';

let partyRef;
let childrenAgesRefs;
const smBreakpoint = 768;
const dimensionTypes = 'D_PCM_Hotel_Facilities,D_Destination,D_DepartureAirport,D_BoardBasis,D_Nights';

export const TYPES = {
  DAY_OF_MONTH_REQUEST: 'DAY_OF_MONTH_REQUEST',
  DAY_OF_MONTH_SUCCESS: 'DAY_OF_MONTH_SUCCESS',
  DAY_OF_MONTH_FAILURE: 'DAY_OF_MONTH_FAILURE',
};

export const suggestHotels = searchConfig => (dispatch, getState) => {
  dispatch(appStateActions.loadingStartDelayedSpinner());
  return dispatch(searchActions.suggestHotels(searchConfig))
    .then(() => {
      const results = getState().search.toArray();
      dispatch(appStateActions.setHasNoHotelResults(results.length === 0));
      dispatch(appStateActions.loadingFinish());
    });
};

export const getDimensions = (dispatch, getState) => {
  const baseState = getState().baseState;
  dispatch(dimensionsActions.loadDimensions({
    dimensions: dimensionTypes,
    state: baseState.get('currentStateEncoded'),
  }));
};

export const getAvailability = (dispatch, getState) => {
  const baseState = getState().baseState;
  dispatch(dimensionsAvailabilityActions.loadDimensionsAvailability({
    dimensions: dimensionTypes,
    state: baseState.get('currentStateEncoded'),
  }));
};

export const updateBaseStateAndGetAvailability = (requestObj, dispatch, getState, opts = {}) => {
  const baseState = getState().baseState;
  const state = baseState.get('currentStateEncoded', '');

  const calDate = baseState.getIn(['queryState', 'props', 'calDate', 'value']);
  const calDateSelected = baseState.getIn(['queryState', 'props', 'calDate', 'selected']);

  if (!opts.forceReset && calDateSelected && !requestObj.deptDate && !requestObj.calDate) {
    requestObj.deptDate = requestObj.deptDate || calDate;
    requestObj.calDate = requestObj.calDate || calDate;
  }

  const hotelOnlyPanda = window.location.href.includes('view=HO');
  const isMobileBreakpoint = getState().appState.get('isMobileBreakpoint');

  dispatch(appStateActions.loadingStart());
  dispatch(baseStateActions.setRoomsValidity(true));
  dispatch(popoverActions.hidePopover());
  return dispatch(baseStateActions.loadBaseState(Object.assign({ state }, requestObj)))
    .then(() => {
      if (!hotelOnlyPanda && !isMobileBreakpoint) {
        dispatch(getAvailability);
      }
    })
    .then(() => dispatch(appStateActions.loadingFinish()))
    .then(() => {
      if (opts.externalCallback) {
        opts.externalCallback();
      }
    });
};

export const setDuration = (duration, isFlex) => (dispatch, getState) => {
  const baseState = getState().baseState;
  const currentDuration = baseState.getIn(['D_Nights', 0]);
  updateBaseStateAndGetAvailability({
    remove: currentDuration,
    add: duration,
    nightFlex: isFlex ? 1 : 'removed',
  }, dispatch, getState);
};

export const addDestination = dimId => (dispatch, getState) =>
  updateBaseStateAndGetAvailability({
    add: dimId,
  }, dispatch, getState);

export const removeDestination = dimId => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    remove: dimId,
  }, dispatch, getState);
};

export const addDepartureAirport = dimId => (dispatch, getState) => {
  const baseState = getState().baseState;
  const requestObj = {};

  if (baseState.get('D_DepartureAirport', []).includes(dimId)) {
    requestObj.remove = dimId;
  } else {
    requestObj.add = dimId;
  }

  updateBaseStateAndGetAvailability(requestObj, dispatch, getState);
};

export const removeDepartureAirport = dimId => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    remove: dimId,
  }, dispatch, getState);
};

export const clearDepartureDate = () => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    deptDate: 'removed',
    calDate: 'removed',
  }, dispatch, getState);
};

export const clearDepartureDateAndFlexibility = () => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    deptDate: 'removed',
    calDate: 'removed',
    deptDateFlexibility: 'removed',
  }, dispatch, getState);
};

export const setDepartureDate = (calDate, externalCallback) => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    deptDate: calDate,
    calDate,
  }, dispatch, getState, { externalCallback });
};

export const setDepartureDateFlexibility = flexibility => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    deptDateFlexibility: flexibility,
  }, dispatch, getState);
};

export const setDepartureDateAndFlexibility = (calDate, flexibility) => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    deptDateFlexibility: flexibility,
    deptDate: calDate,
    calDate,
  }, dispatch, getState);
};

export const addRoom = () => (dispatch, getState) => {
  const baseState = getState().baseState;
  const rooms = baseState.getIn(['queryState', 'props', 'rooms'], Immutable.List());

  updateBaseStateAndGetAvailability({
    [`rooms[${rooms.size}].nAdt`]: 2,
  }, dispatch, getState);
};

export const removeRoom = roomNumber => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({
    [`rooms[${roomNumber}]`]: 'removed',
  }, dispatch, getState);
};

export const resetSearchForm = () => (dispatch, getState) => {
  updateBaseStateAndGetAvailability({ state: '' }, dispatch, getState, { forceReset: true });
};

export const getSearchUnitData = state => (dispatch) => {
  dispatch(appStateActions.loadingStart());
  dispatch(baseStateActions.loadBaseState({
    state: state || LHX.state,
  }))
    .then(() => dispatch(getDimensions))
    .then(() => dispatch(getAvailability))
    .then(() => dispatch(appStateActions.loadingFinish()));
};

export const updateSearchUnitData = () => (dispatch, getState) => {
  const baseState = getState().baseState;

  const requestObj = {
    state: baseState.get('currentStateEncoded', ''),
  };

  baseState.getIn(['queryState', 'props', 'rooms'], Immutable.List())
    .forEach((room, roomNum) => {
      requestObj[`rooms[${roomNum}].nAdt`] = room.getIn(['nAdt', 'value']);

      const childrenAgesString = room.getIn(['cdn', 'arrayValue'], Immutable.List())
        .toJS().join(',');
      requestObj[`rooms[${roomNum}].cdn`] = childrenAgesString;
    });


  updateBaseStateAndGetAvailability(requestObj, dispatch, getState);
};

function numberOfAdultsInRoom(baseState, roomNum) {
  return baseState.getIn(['queryState', 'props', 'rooms', Number(roomNum), 'nAdt', 'value']);
}

function getChildAgesInRoom(baseState, roomNum) {
  return baseState.getIn(['queryState', 'props', 'rooms', Number(roomNum), 'cdn', 'arrayValue'], Immutable.List()).toJS();
}

function numberOfGuestsInRoom(baseState, roomNum) {
  const childAges = getChildAgesInRoom(baseState, roomNum);
  const numberOfAdults = numberOfAdultsInRoom(baseState, roomNum);

  return numberOfAdults + childAges.length;
}

function numberOfInfantsInRoom(baseState, roomNum) {
  const childAges = getChildAgesInRoom(baseState, roomNum);

  return childAges.filter(childAge => childAge < 2 && childAge >= 0).length;
}

function areAllChildAgesSetInRoom(baseState, roomNum) {
  const childAges = getChildAgesInRoom(baseState, roomNum);
  const numChildren = childAges.length;

  return range(0, numChildren)
    .every(index => childAges[index] !== '-1' && childAges[index] !== undefined);
}

function areAllChildAgesUnsetInRoom(baseState, roomNum) {
  const childAges = getChildAgesInRoom(baseState, roomNum);
  const numChildren = childAges.length;

  return range(0, numChildren)
    .every(index => childAges[index] === '-1' || childAges[index] === undefined);
}

function showPartySizePopover(appState) {
  const content = {
    id: 'partySize',
    title: 'Party Size',
    body: <p>Bookings are limited to {config.maxRoomGuests} people per room.</p>,
  };

  const viewportWidth = appState.getIn(['viewportDimensions', 'width']);

  return popoverActions.showPopover({
    position: viewportWidth >= smBreakpoint ? 'left' : 'top',
    relativeRef: partyRef,
    yOffset: 35,
  }, content);
}

function showInfantsPopover(appState, baseState, roomNum) {
  const firstIndexOfInfantField = baseState
    .getIn(['queryState', 'props', 'rooms', `${roomNum}`, 'cdn', 'arrayValue'], Immutable.List())
    .toJS()
    .findIndex(childAge => childAge < 2);

  const content = {
    id: 'infants',
    title: 'Infants',
    body: <p>The number of infants on a booking must be equal to or less than the number of adults. Infants must be placed on the lap of an adult at all times during a flight.</p>,
  };

  const firstInfantField = childrenAgesRefs[firstIndexOfInfantField];
  const viewportWidth = appState.getIn(['viewportDimensions', 'width']);
  const popoverConfig = { relativeRef: firstInfantField };

  if (viewportWidth >= smBreakpoint) {
    popoverConfig.position = 'left';
    popoverConfig.yOffset = 45;
  }

  return popoverActions.showPopover(popoverConfig, content);
}

function showChildAgesPopover(appState) {
  const content = {
    id: 'childAges',
    title: 'Infant and Child Ages',
    body: <p>You must enter the age of all infants and children on the date of return to the UK.</p>,
  };

  const viewportWidth = appState.getIn(['viewportDimensions', 'width']);
  const firstChildAge = childrenAgesRefs[0];
  const popoverConfig = { relativeRef: firstChildAge };

  if (viewportWidth >= smBreakpoint) {
    popoverConfig.position = 'left';
    popoverConfig.yOffset = 45;
  }

  return popoverActions.showPopover(popoverConfig, content);
}

export const validateRoom = roomNum => (dispatch, getState) => {
  let isValid = false;
  const { baseState, appState } = getState();
  const numberOfAdults = numberOfAdultsInRoom(baseState, roomNum);
  const numberOfChildren = getChildAgesInRoom(baseState, roomNum).length;

  if (numberOfGuestsInRoom(baseState, roomNum) > config.maxRoomGuests) {
    dispatch(showPartySizePopover(appState));
  } else if (numberOfInfantsInRoom(baseState, roomNum) > numberOfAdults) {
    dispatch(showInfantsPopover(appState, baseState, roomNum));
  } else if (numberOfChildren && areAllChildAgesUnsetInRoom(baseState, roomNum)) {
    dispatch(showChildAgesPopover(appState));
  } else if (numberOfChildren && !areAllChildAgesUnsetInRoom(baseState, roomNum) && !areAllChildAgesSetInRoom(baseState, roomNum)) {
    dispatch(popoverActions.hidePopover());
  } else if (areAllChildAgesSetInRoom(baseState, roomNum)) {
    dispatch(updateSearchUnitData());
    dispatch(popoverActions.hidePopover());
    isValid = true;
  }

  dispatch(baseStateActions.setRoomsValidity(isValid));
};

export const changeNumberOfAdults = (roomNum, numAdults, partyElementRef, childrenAgesElementRefs) => (dispatch) => {
  dispatch(baseStateActions.changeNumberOfAdults(roomNum, numAdults));

  partyRef = partyElementRef;
  childrenAgesRefs = childrenAgesElementRefs;
  dispatch(validateRoom(roomNum));
};

export const changeNumberOfChildren = (roomNum, numChildren, partyElementRef, childrenAgesElementRefs) => (dispatch) => {
  dispatch(baseStateActions.changeNumberOfChildren(roomNum, numChildren));

  partyRef = partyElementRef;
  childrenAgesRefs = childrenAgesElementRefs;
  dispatch(validateRoom(roomNum));
};

export const changeChildAge = (roomNum, childNumber, childAge, partyElementRef, childrenAgesElementRefs) => (dispatch) => {
  dispatch(baseStateActions.changeChildAge(roomNum, childNumber, childAge));

  partyRef = partyElementRef;
  childrenAgesRefs = childrenAgesElementRefs;
  dispatch(validateRoom(roomNum));
};

export const resetChildCountIfNotAllAgesSet = roomNum => (dispatch, getState) => {
  const baseState = getState().baseState;

  if (!areAllChildAgesSetInRoom(baseState, roomNum)) {
    dispatch(changeNumberOfChildren(roomNum, 0, partyRef, childrenAgesRefs));
  }
};

export const suggestDestinations = searchConfig => (dispatch, getState) => {
  dispatch(appStateActions.loadingStartDelayedSpinner());

  return dispatch(searchActions.suggestDestinations(searchConfig))
    .then(() => {
      const results = getState().search.toArray();
      dispatch(appStateActions.setHasNoDestinationResults(results.length === 0));
      dispatch(appStateActions.loadingFinish());
    });
};

export const loadDayOfMonthData = deptDate => (dispatch, getState) => {
  const baseState = getState().baseState;
  const params = {
    url: '/holidays/',
    state: baseState.get('currentStateEncoded', ''),
    deptDate,
    deptDateFlexibility: -2,
    expose: 'D_DayOfMonth',
  };

  return dispatch({
    [CALL_API]: {
      types: [
        TYPES.DAY_OF_MONTH_REQUEST,
        TYPES.DAY_OF_MONTH_SUCCESS,
        TYPES.DAY_OF_MONTH_FAILURE,
      ],
      schema: Schemas.DAY_OF_MONTH,
      endpoint: `/bridge/dayOfMonth.json?${serialize(params)}`,
    },
  });
};



/** WEBPACK FOOTER **
 ** ./src/redux/actions/searchUnitActions.js
 **/